import React, { useState } from 'react';
import { X, ExternalLink, Plus, Lock, Trash2, Loader2 } from 'lucide-react';
import FormFieldList from '../shared/FormFieldList';
import { DesktopPropertyResponsesSection } from '../../formResponses/PropertyResponsesSection';
import TagsInput from '../../ui/TagsInput';
import Toast from '../../Toast/Toast';

const DesktopPropertyDrawer = ({ propertyTab, availableTags }) => {
  const [deleteFieldConfirm, setDeleteFieldConfirm] = useState(null);
  const [isFieldDeleting, setIsFieldDeleting] = useState(false);
  const [toast, setToast] = useState(null);

  if (!propertyTab.showPropertyDrawer) return null;

  const { currentProperty, handleCloseDrawer, activeDrawerTab, setActiveDrawerTab, handleUpdateField, handleDeleteProperty, isDirty, saving, handleSaveProperty } = propertyTab;

  const onSave = async () => {
    const ok = await handleSaveProperty();
    setToast(ok
      ? { message: 'Property saved successfully', type: 'success' }
      : { message: 'Failed to save property', type: 'error' }
    );
  };

  const handleFieldDeleteClick = (fieldName) => {
    setDeleteFieldConfirm(fieldName);
  };

  const handleConfirmFieldDelete = async () => {
    if (!deleteFieldConfirm) return;
    setIsFieldDeleting(true);
    try {
      const success = propertyTab.handleRemoveFormField(deleteFieldConfirm);
      if (success === false) {
        setToast({ message: 'Cannot delete this field.', type: 'error' });
      } else {
        setToast({ message: 'Custom field deleted successfully', type: 'success' });
      }
    } catch {
      setToast({ message: 'Failed to delete field. Please try again.', type: 'error' });
    } finally {
      setIsFieldDeleting(false);
      setDeleteFieldConfirm(null);
    }
  };

  const mergeCaptureFields = () => {
    const existing = currentProperty.captureFormFields || [];
    const required = [
      { name: 'First Name', locked: true },
      { name: 'Last Name', locked: true },
      { name: 'Email', locked: true },
      { name: 'Phone', locked: true },
      { name: 'Address', locked: true },
    ];
    const merged = [...required];
    existing.forEach((f) => {
      const fieldName = (f.name || '').toLowerCase();
      // Skip legacy 'Name' field — replaced by First Name + Last Name
      if (fieldName === 'name') return;
      if (!merged.some(m => m.name.toLowerCase() === fieldName)) {
        merged.push(f);
      }
    });
    return merged;
  };

  return (
    <>
      <div className="fixed inset-0 bg-stone-900/10 backdrop-blur-sm z-40" onClick={handleCloseDrawer}></div>
      <div className="fixed inset-y-0 right-0 w-[900px] bg-stone-50 shadow-2xl z-50 flex flex-col border-l border-notion-border">
        <div className="px-6 py-4 border-b border-notion-border bg-white flex justify-between items-center flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-notion-text">{currentProperty.address || 'Untitled Property'}</h2>
            <a
              href={`${window.location.origin}/property/${currentProperty.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-stone-400 hover:text-stone-600 flex items-center gap-1 mt-0.5"
            >
              <ExternalLink className="w-3 h-3" /> {`${window.location.host}/property/${currentProperty.id}`}
            </a>
          </div>
          <div className="flex items-center gap-3">
            {isDirty && !saving && (
              <span className="text-[10px] text-amber-600 font-medium">Unsaved changes</span>
            )}
            <button
              onClick={onSave}
              disabled={!isDirty || saving}
              className="px-3 py-1.5 bg-stone-800 text-white rounded text-xs font-medium hover:bg-stone-700 transition flex items-center gap-1.5 disabled:bg-stone-400 disabled:cursor-not-allowed"
            >
              {saving && <Loader2 className="w-3 h-3 animate-spin" />}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button onClick={handleDeleteProperty} className="px-3 py-1.5 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 transition flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
            <button onClick={handleCloseDrawer} className="text-stone-400 hover:text-stone-600">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Editor Panel */}
          <div className="w-1/2 flex flex-col border-r border-notion-border bg-white">
            <div className="flex border-b border-notion-border">
              {['editor', 'form', 'responses'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveDrawerTab(tab)}
                  className={`flex-1 py-3 text-xs font-medium transition-colors ${activeDrawerTab === tab
                    ? 'bg-notion-hover text-notion-text font-semibold'
                    : 'text-notion-text-light hover:text-notion-text hover:bg-notion-sidebar'
                    }`}
                >
                  {tab === 'editor' ? 'Page Editor' : tab === 'form' ? 'Form & Logic' : `Responses (${propertyTab.responseCount})`}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {activeDrawerTab === 'editor' && (
                <>
                  <div className="space-y-4">
                    <h3 className="text-xs font-bold text-stone-400 uppercase tracking-wider">Property Details</h3>
                    <div className="space-y-2">
                      <label className="block text-[11px] text-stone-500 font-medium mb-1">Address Line 1</label>
                      <input
                        type="text"
                        placeholder="Street Address"
                        value={currentProperty.address || ''}
                        onChange={(e) => handleUpdateField('address', e.target.value)}
                        className="w-full bg-notion-sidebar border-transparent rounded px-3 py-2 text-xs outline-none focus:bg-white focus:border-stone-300 border transition"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-[11px] text-stone-500 font-medium mb-1">Address Line 2</label>
                      <input
                        type="text"
                        placeholder="Apartment, Suite, etc. (optional)"
                        value={currentProperty.address2 || ''}
                        onChange={(e) => handleUpdateField('address2', e.target.value)}
                        className="w-full bg-notion-sidebar border-transparent rounded px-3 py-2 text-xs outline-none focus:bg-white focus:border-stone-300 border transition"
                      />
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className="block text-[11px] text-stone-500 font-medium mb-1">Price</label>
                        <input
                          type="text"
                          placeholder="Price"
                          value={currentProperty.price || ''}
                          onChange={(e) => handleUpdateField('price', e.target.value)}
                          className="w-full bg-notion-sidebar border-transparent rounded px-3 py-2 text-xs outline-none"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-[11px] text-stone-500 font-medium mb-1">Sq Ft</label>
                        <input
                          type="text"
                          placeholder="Sq Ft"
                          value={currentProperty.sqft || ''}
                          onChange={(e) => handleUpdateField('sqft', e.target.value)}
                          className="w-full bg-notion-sidebar border-transparent rounded px-3 py-2 text-xs outline-none"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className="block text-[11px] text-stone-500 font-medium mb-1">Bedrooms</label>
                        <input
                          type="number"
                          min="0"
                          placeholder="Bedrooms"
                          value={currentProperty.bedrooms || 0}
                          onChange={(e) => handleUpdateField('bedrooms', parseInt(e.target.value) || 0)}
                          className="w-full bg-notion-sidebar border-transparent rounded px-3 py-2 text-xs outline-none"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-[11px] text-stone-500 font-medium mb-1">Bathrooms</label>
                        <input
                          type="number"
                          min="0"
                          placeholder="Bathrooms"
                          value={currentProperty.bathrooms || 0}
                          onChange={(e) => handleUpdateField('bathrooms', parseInt(e.target.value) || 0)}
                          className="w-full bg-notion-sidebar border-transparent rounded px-3 py-2 text-xs outline-none"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="block text-[11px] text-stone-500 font-medium mb-1">Description</label>
                      <textarea
                        placeholder="Description"
                        rows="4"
                        value={currentProperty.description || ''}
                        onChange={(e) => handleUpdateField('description', e.target.value)}
                        className="w-full bg-notion-sidebar border-transparent rounded px-3 py-2 text-xs outline-none resize-none"
                      />
                    </div>
                  </div>
                  <div className="mt-6">
                    <h3 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-3">Photos ({currentProperty.imageUrls?.length || 0} of 15)</h3>
                    <div className="grid grid-cols-4 gap-2">
                      {currentProperty.imageUrls?.slice(0, 4).map((url, i) => (
                        <div
                          key={i}
                          className="aspect-square bg-stone-100 rounded border border-stone-200 bg-cover bg-center"
                          style={{ backgroundImage: `url('${url}')` }}
                        ></div>
                      ))}
                      {(currentProperty.imageUrls?.length || 0) < 15 && (
                        <label className="aspect-square border border-dashed border-stone-300 rounded flex items-center justify-center text-stone-400 hover:border-stone-500 cursor-pointer transition">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              if (e.target.files?.[0]) {
                                propertyTab.handleUploadImage(e.target.files[0]);
                              }
                            }}
                            className="hidden"
                          />
                          <Plus className="w-4 h-4" />
                        </label>
                      )}
                    </div>
                  </div>
                </>
              )}

              {activeDrawerTab === 'form' && (
                <>
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <h3 className="text-xs font-bold text-stone-400 uppercase tracking-wider">Capture Form Fields</h3>
                      <div className="flex flex-col gap-2">
                        {mergeCaptureFields().map((field, idx) => (
                          <div key={idx} className="relative group">
                            <input
                              type="text"
                              value={field.name}
                              readOnly
                              disabled={field.locked}
                              className={`w-full border border-notion-border rounded px-3 py-2 text-xs outline-none ${field.locked ? 'bg-stone-100 text-stone-400' : 'bg-white text-stone-600 pr-8'}`}
                            />
                            {field.locked ? (
                              <Lock className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-300" />
                            ) : (
                              <button
                                onClick={() => handleFieldDeleteClick(field.name)}
                                title="Delete field"
                                className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-stone-300 hover:text-red-500 transition"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        ))}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="Add custom field (e.g. Move-in Date)"
                            className="flex-1 bg-white border border-notion-border rounded px-3 py-2 text-xs outline-none"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && e.target.value.trim()) {
                                propertyTab.handleAddFormField(e.target.value);
                                e.target.value = '';
                              }
                            }}
                          />
                          <button
                            className="px-3 py-2 bg-stone-800 text-white rounded text-xs font-medium hover:bg-stone-700 transition"
                            onClick={(e) => {
                              const input = e.currentTarget.previousElementSibling;
                              if (input.value.trim()) {
                                propertyTab.handleAddFormField(input.value);
                                input.value = '';
                              }
                            }}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Post-Submission Automations</h3>
                      <div className="space-y-3">
                        <label className="flex items-center gap-3 p-3 border border-notion-border rounded-lg bg-white cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!currentProperty.automations?.createLead}
                            onChange={(e) => propertyTab.handleToggleAutomation('createLead', e.target.checked)}
                            className="accent-stone-800 w-4 h-4 rounded"
                          />
                          <div className="flex flex-col">
                            <span className="text-xs font-medium text-stone-700">Create Lead in CRM</span>
                            <span className="text-[10px] text-stone-400">Automatically create a lead when this form is submitted</span>
                          </div>
                        </label>

                        {/* Auto-assign tags when createLead is enabled */}
                        {!!currentProperty.automations?.createLead && (
                          <div className="ml-7 mt-1">
                            <label className="block text-[10px] text-stone-500 font-medium mb-1.5">Auto-assign Tags</label>
                            <TagsInput
                              tags={currentProperty.automations?.tags || []}
                              onChange={(newTags) => propertyTab.handleAutomationTagsChange(newTags)}
                              suggestions={availableTags}
                              placeholder="Add tag and press Enter..."
                              size="sm"
                            />
                            <p className="text-[10px] text-stone-400 mt-1">Tags will be applied to leads created from this form</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {activeDrawerTab === 'responses' && (
                <DesktopPropertyResponsesSection
                  propertyId={currentProperty.id}
                  propertyName={currentProperty.address || 'Untitled Property'}
                />
              )}
            </div>
          </div>

          {/* Phone Preview */}
          <div className="w-1/2 bg-stone-100 flex items-center justify-center p-8">
            <div className="phone-frame w-[280px] h-[580px] border-[10px] border-stone-800 rounded-[30px] overflow-hidden bg-white relative flex flex-col">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-24 h-5 bg-stone-800 rounded-b-xl z-10"></div>
              <div className="h-full w-full overflow-y-auto pt-8">
                <div className="h-36 bg-stone-300 w-full mb-4 relative">
                  <div className="absolute bottom-2 left-2 text-white font-bold text-lg drop-shadow-md">$450,000</div>
                </div>
                <div className="px-4 space-y-4">
                  <div>
                    <h1 className="font-bold text-stone-800 text-base leading-tight">123 Main St</h1>
                    <p className="text-[10px] text-stone-500">Scranton, PA 18503</p>
                  </div>
                  <div className="flex gap-2 text-[9px] text-stone-600 font-medium">
                    <span className="bg-stone-100 px-2 py-1 rounded">3 Beds</span>
                    <span className="bg-stone-100 px-2 py-1 rounded">2 Baths</span>
                    <span className="bg-stone-100 px-2 py-1 rounded">2,400 Sq Ft</span>
                  </div>
                  <div className="bg-stone-50 rounded-lg p-3 border border-stone-100 mt-4">
                    <h3 className="font-bold text-stone-800 text-[10px] mb-2">Schedule a Viewing</h3>
                    <div className="space-y-1.5">
                      <div className="h-7 bg-white border border-stone-200 rounded w-full flex items-center px-2"><span className="text-[9px] text-stone-300">Name</span></div>
                      <div className="h-7 bg-white border border-stone-200 rounded w-full flex items-center px-2"><span className="text-[9px] text-stone-300">Email</span></div>
                      <div className="h-7 bg-stone-800 rounded w-full mt-1 flex items-center justify-center"><span className="text-[9px] text-white">Submit Interest</span></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Field Confirmation Modal */}
      {deleteFieldConfirm && (
        <div className="fixed inset-0 bg-stone-900/20 flex items-center justify-center z-[60]" onClick={() => !isFieldDeleting && setDeleteFieldConfirm(null)}>
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-stone-800 mb-2">Delete Custom Field</h3>
            <p className="text-sm text-stone-600 mb-1">
              Are you sure you want to delete <strong>{deleteFieldConfirm}</strong>?
            </p>
            <p className="text-xs text-stone-400 mb-4">
              All future form submissions will no longer include this field.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setDeleteFieldConfirm(null)}
                disabled={isFieldDeleting}
                className="flex-1 py-2 border border-notion-border rounded text-stone-600 text-sm font-medium hover:bg-stone-50 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmFieldDelete}
                disabled={isFieldDeleting}
                className="flex-1 py-2 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition"
              >
                {isFieldDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </>
  );
};

export default React.memo(DesktopPropertyDrawer);
